// PayPalOverlay.js
import React, { useEffect, useRef, useState } from 'react';

export default function PayPalOverlay({ email, onClose, setCredits }) {
  const [tiersData, setTiersData] = useState(null); // { currencies, defaultCurrency, tiers, paypalClientID }
  const [currency, setCurrency] = useState('USD');
  const [selectedTier, setSelectedTier] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [status, setStatus] = useState('init'); // optional debug

  const containerRef = useRef(null);
  const buttonsRef = useRef(null);
  const loadedCurrenciesRef = useRef(new Set()); // prevent duplicate SDK loads

  // Hold latest callbacks in refs so effects don't depend on changing identities
  const onCloseRef = useRef(onClose);
  const setCreditsRef = useRef(setCredits);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => { setCreditsRef.current = setCredits; }, [setCredits]);

  // 1) Load tiers from backend
  useEffect(() => {
    (async () => {
      try {
        setStatus('fetching-tiers');
        const res = await fetch('/paypal/tiers', { cache: 'no-store' });
        if (!res.ok) throw new Error(`tiers ${res.status}`);
        const data = await res.json();
        setTiersData(data);
        if (data?.defaultCurrency) setCurrency(data.defaultCurrency);
        setStatus('tiers-loaded');
      } catch (e) {
        console.error('❌ Failed to load tiers:', e);
        setStatus('tiers-error');
        alert('Failed to load PayPal tiers. Please try again later.');
      }
    })();
  }, []);

  // 2) Load SDK per currency with a unique namespace
  useEffect(() => {
    if (!tiersData) return;

    const NAMESPACE = `paypal_${currency}`;
    const SCRIPT_ID = `paypal-sdk-${currency}`;

    setSdkReady(false);
    setStatus(`sdk-loading-${currency}`);

    // Already ready for this currency?
    if (window[NAMESPACE]?.Buttons) {
      setSdkReady(true);
      setStatus(`sdk-ready-${currency}`);
      return;
    }

    // If script tag already exists (e.g., due to Strict Mode or prior mounts), just await it
    if (loadedCurrenciesRef.current.has(currency)) {
      const existing = document.getElementById(SCRIPT_ID);
      if (existing) {
        existing.addEventListener(
          'load',
          () => {
            setSdkReady(true);
            setStatus(`sdk-ready-${currency}`);
          },
          { once: true }
        );
      }
      return;
    }

    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = `https://www.paypal.com/sdk/js?client-id=${tiersData.paypalClientID}&currency=${currency}&intent=capture&components=buttons`;
    s.async = true;
    s.setAttribute('data-namespace', NAMESPACE);

    s.onload = () => {
      loadedCurrenciesRef.current.add(currency);
      setSdkReady(true);
      setStatus(`sdk-ready-${currency}`);
    };
    s.onerror = (e) => {
      console.error('❌ PayPal SDK load error for', s.src, e);
      setSdkReady(false);
      setStatus(`sdk-error-${currency}`);
      alert('Could not load PayPal. Check your network/ad-blockers.');
    };
    document.body.appendChild(s);
  }, [currency, tiersData]);

  // 3) Render buttons when SDK is ready AND a tier is selected
  //    IMPORTANT: Do NOT depend on changing function identities (onClose / setCredits)
  //    We also avoid teardown on each re-render; we only close when (currency,tier) changes or on unmount.
  const renderKeyRef = useRef(null); // `${currency}:${selectedTier?.credits}`
  useEffect(() => {
    const mount = async () => {
      if (!sdkReady || !selectedTier || !containerRef.current) return;

      const priceStr = selectedTier?.price?.[currency];
      if (!priceStr) {
        console.warn('No price for selected currency:', currency, selectedTier);
        return;
      }

      const NAMESPACE = `paypal_${currency}`;
      const paypalNS = window[NAMESPACE];
      if (!paypalNS || !paypalNS.Buttons) {
        console.warn('PayPal namespace not ready:', NAMESPACE, window[NAMESPACE]);
        return;
      }

      const newKey = `${currency}:${selectedTier.credits}`;
      // If the same key is already rendered, skip re-render (prevents loops on parent rerenders)
      if (renderKeyRef.current === newKey && buttonsRef.current) {
        setStatus('buttons-intact');
        return;
      }

      // If we are switching currency/tier: close previous instance and clear container
      if (buttonsRef.current) {
        try { await buttonsRef.current.close(); } catch {}
        buttonsRef.current = null;
      }
      if (containerRef.current) containerRef.current.innerHTML = '';

      const buttons = paypalNS.Buttons({
        // SERVER-SIDE CREATE
        createOrder: async () => {
          try {
            setStatus('create-order');
            const res = await fetch('/paypal/create-order', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                amount: priceStr,
                currency,
                credits: selectedTier.credits,
                email
              }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.id) {
              console.error('❌ create-order failed:', data);
              setStatus('create-order-failed');
              throw new Error('create-order failed');
            }
            setStatus('order-created');
            return data.id;
          } catch (err) {
            console.error('❌ /paypal/create-order error:', err);
            alert('Could not start the payment. Please try again.');
            throw err;
          }
        },

        // SERVER-SIDE CAPTURE
        onApprove: async (data) => {
          try {
            setStatus('capture-order');
            const res = await fetch('/paypal/capture-order', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                orderID: data.orderID,
                email,
                credits: selectedTier.credits,
              }),
            });
            const payload = await res.json().catch(() => ({}));
            if (!res.ok) {
              console.error('❌ capture-order failed:', payload);
              setStatus('capture-failed');
              alert('Payment capture failed.');
              return;
            }
            setStatus('capture-ok');
            alert(`Success! ${selectedTier.credits} credits added to your account.`);
            if (typeof setCreditsRef.current === 'function') {
              setCreditsRef.current((prev) => prev + selectedTier.credits);
            }
            if (typeof onCloseRef.current === 'function') {
              onCloseRef.current();
            }
          } catch (err) {
            console.error('❌ onApprove fetch error:', err);
            setStatus('capture-exception');
            alert('Something went wrong with the PayPal payment.');
          }
        },

        onError: (err) => {
          console.error('❌ PayPal onError:', err);
          setStatus('buttons-error');
          alert('Something went wrong with the PayPal payment.');
        },

        onCancel: (data) => {
          console.warn('⚠️ Payment cancelled:', data);
          setStatus('cancelled');
        },

        style: { layout: 'vertical', height: 45 },
      });

      if (!buttons.isEligible()) {
        console.warn('[paypal] Buttons not eligible in this context');
        setStatus('not-eligible');
        containerRef.current.innerHTML =
          '<div style="padding:8px;color:#b91c1c">PayPal buttons are not eligible here (browser/privacy setting?).</div>';
        renderKeyRef.current = null;
        return;
      }

      setStatus('rendering-buttons');
      await buttons.render(containerRef.current).catch((e) => {
        console.error('❌ buttons.render error:', e);
        setStatus('render-error');
        alert('Could not render PayPal button. Check console for details.');
      });

      buttonsRef.current = buttons;
      renderKeyRef.current = newKey;
      setStatus('buttons-rendered');
    };

    mount();

    // IMPORTANT: no cleanup here — we manage closing only when key changes, and on unmount:
    // This avoids close→re-render loops triggered by unrelated parent renders.
  }, [sdkReady, selectedTier, currency, email]); // <- minimal, stable deps

  // Close buttons ONLY on unmount (not on every render)
  useEffect(() => {
    return () => {
      try { buttonsRef.current?.close?.(); } catch {}
      buttonsRef.current = null;
      renderKeyRef.current = null;
      if (containerRef.current) containerRef.current.innerHTML = '';
    };
  }, []);

  const currencies = tiersData?.currencies || ['USD'];
  const tiers = tiersData?.tiers || [];

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        style={{
          position: 'relative',
          background: 'white',
          width: '520px',
          minHeight: '440px',
          borderRadius: '12px',
          padding: '2vw',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <button
          onClick={() => onCloseRef.current?.()}
          style={{
            position: 'absolute',
            top: 10,
            right: 14,
            background: 'transparent',
            border: 'none',
            fontSize: '1.2rem',
            cursor: 'pointer',
          }}
          aria-label="Close"
        >
          ✖
        </button>

        <h3 style={{ marginBottom: 0, fontSize: '1.6rem' }}>Buy Credits</h3>
        {/* <div style={{ fontSize: 12, color: '#64748b' }}>status: {status}</div> */}

        {/* Currency selector */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label htmlFor="currency" style={{ fontWeight: 'bold' }}>Currency:</label>
          <select
            id="currency"
            value={currency}
            onChange={(e) => {
              setSelectedTier(null);
              setCurrency(e.target.value);
            }}
            style={{ padding: '6px 10px', borderRadius: 6 }}
          >
            {currencies.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {/* Tiers from backend */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', alignItems: 'center' }}>
          {tiers.map((t) => (
            <button
              key={`${t.credits}`}
              onClick={() => setSelectedTier(t)}
              style={{
                padding: '10px 12px',
                borderRadius: 8,
                backgroundColor: selectedTier?.credits === t.credits ? '#0ea5e9' : '#e2e8f0',
                color: selectedTier?.credits === t.credits ? 'white' : '#1e293b',
                border: 'none',
                fontWeight: 'bold',
                fontSize: '1rem',
                cursor: 'pointer',
                width: '80%',
                textAlign: 'center',
              }}
            >
              {t.credits} credits – {currency} {t.price?.[currency]}
            </button>
          ))}
        </div>

        {/* PayPal buttons mount point */}
        <div
          ref={containerRef}
          id="paypal-buttons-container"
          style={{ marginTop: '0.5rem', width: '100%', minHeight: 60 }}
        />
      </div>
    </div>
  );
}
